/**
 * Calendar arithmetic for the timeline and the Gantt chart.
 *
 * A date is a whole number of days counted from 1970-01-01 in the proleptic Gregorian
 * calendar, so spacing events in proportion to time is subtraction. Years are
 * astronomical internally — 1 BCE is year 0 and 44 BCE is year −43 — because the BCE/CE
 * numbering has no year zero and arithmetic across the boundary would be off by one.
 * Conversion to the historians' numbering happens only when a year is written out.
 *
 * Dates are taken as written: the tool does not convert Julian-calendar dates, so 15
 * March 44 BCE is placed where the proleptic Gregorian 15 March 44 BCE would be — a
 * difference of two days that no timeline can show.
 */

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

export function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return MONTH_DAYS[month - 1] ?? 30;
}

/**
 * Days from 1970-01-01 to `year`-`month`-`day` (Howard Hinnant's `days_from_civil`).
 * Exact for every astronomical year, negative ones included.
 */
export function dayNumber(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const shiftedMonth = (month + 9) % 12;
  const dayOfYear = Math.floor((153 * shiftedMonth + 2) / 5) + day - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146_097 + dayOfEra - 719_468;
}

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

/** The inverse of `dayNumber`. */
export function civilDate(days: number): CivilDate {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const dayOfEra = z - era * 146_097;
  const yearOfEra = Math.floor(
    (dayOfEra -
      Math.floor(dayOfEra / 1460) +
      Math.floor(dayOfEra / 36_524) -
      Math.floor(dayOfEra / 146_096)) /
      365
  );
  const dayOfYear =
    dayOfEra -
    (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
  const shiftedMonth = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * shiftedMonth + 2) / 5) + 1;
  const month = shiftedMonth < 10 ? shiftedMonth + 3 : shiftedMonth - 9;
  const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
  return { year, month, day };
}

/** ISO weekday: 1 = Monday … 7 = Sunday. 1970-01-01 was a Thursday. */
export function isoWeekday(days: number): number {
  return ((((days + 3) % 7) + 7) % 7) + 1;
}

export function isWeekend(days: number): boolean {
  return isoWeekday(days) >= 6;
}

/** The Monday on or before `days`. */
export function startOfWeek(days: number): number {
  return days - (isoWeekday(days) - 1);
}

/** Months since year 0, January — a single number for month arithmetic. */
export function monthIndex(days: number): number {
  const { year, month } = civilDate(days);
  return year * 12 + (month - 1);
}

/** The first day of month number `index` (see `monthIndex`). */
export function monthStart(index: number): number {
  const year = Math.floor(index / 12);
  return dayNumber(year, index - year * 12 + 1, 1);
}

/* ------------------------------------------------------------- ISO dates */

const ISO_DATE = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/;

/** A `YYYY-MM-DD` date (slashes or dots also accepted), or null. */
export function parseIsoDate(text: string): number | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  return dayNumber(year, month, day);
}

function pad(value: number, width: number): string {
  return String(Math.abs(value)).padStart(width, "0");
}

/** `days` as `YYYY-MM-DD` — for files, which want one unambiguous format. */
export function formatIsoDate(days: number): string {
  const { year, month, day } = civilDate(days);
  const sign = year < 0 ? "-" : "";
  return `${sign}${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/* ------------------------------------------------------- historical dates */

export type DatePrecision = "year" | "month" | "day";

export interface HistoricalDate {
  /** Astronomical year: 1 BCE is 0, 44 BCE is −43. */
  year: number;
  month: number;
  day: number;
  precision: DatePrecision;
  /** Written with "c." or "circa". */
  circa: boolean;
  /** Written before the Common Era. */
  bce: boolean;
}

export type DateProblem = "unreadable" | "yearZero" | "badMonth" | "badDay";

export type DateResult =
  | { ok: true; date: HistoricalDate }
  | { ok: false; problem: DateProblem };

const CIRCA_PREFIX = /^(?:circa\s+|ca\.\s*|ca\s+|c\.\s*|c\s+)/i;
const BCE_SUFFIX = /\s*(?:b\.?\s?c\.?\s?e\.?|b\.?\s?c\.?)$/i;
const CE_SUFFIX = /\s*(?:c\.?\s?e\.?|a\.?\s?d\.?)$/i;
const AD_PREFIX = /^a\.?\s?d\.?\s+/i;
const CORE_DATE = /^(-|−)?(\d{1,6})(?:[-/.](\d{1,2})(?:[-/.](\d{1,2}))?)?$/;

/** Strip the era words, returning whether the date is BCE. */
function readEra(text: string): { rest: string; bce: boolean } {
  if (BCE_SUFFIX.test(text)) {
    return { rest: text.replace(BCE_SUFFIX, "").trim(), bce: true };
  }
  const rest = text.replace(CE_SUFFIX, "").replace(AD_PREFIX, "").trim();
  return { rest, bce: false };
}

/**
 * A year (`1066`), year and month (`1969-07`) or full date (`1969-07-20`), with an
 * optional era — `44 BCE`, `44 BC`, `79 CE`, `AD 79` — and an optional `c.` for an
 * approximate date. A leading minus also means BCE: `-500` is 500 BCE.
 */
export function parseHistoricalDate(input: string): DateResult {
  let text = input.trim();
  const circa = CIRCA_PREFIX.test(text);
  text = text.replace(CIRCA_PREFIX, "");
  const era = readEra(text);
  const match = CORE_DATE.exec(era.rest);
  if (!match) {
    return { ok: false, problem: "unreadable" };
  }
  const bce = era.bce || match[1] !== undefined;
  const written = Number(match[2]);
  if (written === 0) {
    return { ok: false, problem: "yearZero" };
  }
  const year = bce ? 1 - written : written;
  const month = match[3] === undefined ? 1 : Number(match[3]);
  const day = match[4] === undefined ? 1 : Number(match[4]);
  if (month < 1 || month > 12) {
    return { ok: false, problem: "badMonth" };
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    return { ok: false, problem: "badDay" };
  }
  let precision: DatePrecision = "day";
  if (match[3] === undefined) {
    precision = "year";
  } else if (match[4] === undefined) {
    precision = "month";
  }
  return { ok: true, date: { year, month, day, precision, circa, bce } };
}

/** The first day the date covers. */
export function dateStart(date: HistoricalDate): number {
  return dayNumber(date.year, date.month, date.day);
}

/** The first day after the date — the end of the year, month or day it names. */
export function dateEnd(date: HistoricalDate): number {
  if (date.precision === "year") {
    return dayNumber(date.year + 1, 1, 1);
  }
  if (date.precision === "month") {
    return monthStart(date.year * 12 + date.month);
  }
  return dateStart(date) + 1;
}

/** The year as historians write it: `{ year: 44, bce: true }` for astronomical −43. */
export function historicalYear(astronomical: number): {
  year: number;
  bce: boolean;
} {
  return astronomical <= 0
    ? { year: 1 - astronomical, bce: true }
    : { year: astronomical, bce: false };
}

/* --------------------------------------------------------------- ticks */

export type TickUnit = "year" | "month" | "day";

export interface TimeTick {
  /** Where the tick sits. */
  day: number;
  unit: TickUnit;
  /** The calendar date at the tick (astronomical year). */
  date: CivilDate;
}

const YEAR_STEPS = [
  1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10_000,
  20_000, 50_000, 100_000,
];
const MONTH_STEPS = [1, 2, 3, 6];
const DAY_STEPS = [1, 2, 7, 14];
const DAYS_PER_YEAR = 365.2425;

function pickStep(span: number, steps: readonly number[], count: number) {
  return steps.find((step) => span / step <= count) ?? steps.at(-1) ?? 1;
}

/**
 * Year ticks in the historians' numbering. Steps are counted in written years, so the
 * ticks read 500 BCE, 1 CE, 500 CE rather than 501 BCE, 1 BCE, 500 CE: a written BCE
 * year `n` is astronomical `1 − n`, and the era boundary gets a tick at 1 CE.
 */
function yearTicks(min: number, max: number, count: number): TimeTick[] {
  const first = civilDate(min).year;
  const last = civilDate(max).year + 1;
  const step = pickStep(last - first, YEAR_STEPS, count);
  const ticks: TimeTick[] = [];
  // Written years: negative for BCE. Astronomical a → written a ≥ 1 ? a : a − 1.
  const writtenFirst = first >= 1 ? first : first - 1;
  const writtenLast = last >= 1 ? last : last - 1;
  for (
    let written = Math.floor(writtenFirst / step) * step;
    written <= writtenLast + step && ticks.length < 200;
    written += step
  ) {
    let astronomical = written;
    if (written < 0) {
      astronomical = written + 1;
    } else if (written === 0) {
      astronomical = 1;
    }
    const day = dayNumber(astronomical, 1, 1);
    if (day >= min && day <= max) {
      ticks.push({ day, unit: "year", date: civilDate(day) });
    }
  }
  return ticks;
}

function monthTicks(min: number, max: number, count: number): TimeTick[] {
  const first = monthIndex(min);
  const last = monthIndex(max) + 1;
  const step = pickStep(last - first, MONTH_STEPS, count);
  const ticks: TimeTick[] = [];
  for (
    let index = Math.ceil(first / step) * step;
    index <= last;
    index += step
  ) {
    const day = monthStart(index);
    if (day >= min && day <= max) {
      ticks.push({ day, unit: "month", date: civilDate(day) });
    }
  }
  return ticks;
}

function dayTicks(min: number, max: number, count: number): TimeTick[] {
  const step = pickStep(max - min, DAY_STEPS, count);
  const start = step >= 7 ? startOfWeek(min) : Math.floor(min / step) * step;
  const ticks: TimeTick[] = [];
  for (let day = start; day <= max; day += step) {
    if (day >= min) {
      ticks.push({ day, unit: "day", date: civilDate(day) });
    }
  }
  return ticks;
}

/**
 * Ticks for a time axis from day `min` to day `max`: years for spans of a few years or
 * more, months for spans of a few months, days below that — about `count` of them.
 */
export function timeTicks(min: number, max: number, count = 6): TimeTick[] {
  const span = Math.max(1, max - min);
  if (span >= 2.5 * DAYS_PER_YEAR) {
    return yearTicks(min, max, count);
  }
  if (span >= 75) {
    return monthTicks(min, max, count);
  }
  return dayTicks(min, max, count);
}
